/**
 * Content hashing — how the import screen recognises a template it already has.
 *
 * The hash is taken over a CANONICAL serialization, not over the file bytes.
 * The same design exported twice by the same tool routinely differs in key
 * order and whitespace while describing an identical template; hashing the raw
 * text would call those two different templates and duplicate detection would
 * never fire on the one case it exists for.
 */

/**
 * Deterministic JSON: object keys sorted at every depth, no whitespace.
 *
 * Array order is preserved — element order is z-order in this document shape,
 * so two pages holding the same elements stacked differently are genuinely
 * different templates.
 *
 * @param {*} value
 * @returns {string}
 */
export const canonicalize = (value) => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`;

  const body = Object.keys(value)
    .sort()
    .filter((key) => value[key] !== undefined)
    .map((key) => `${JSON.stringify(key)}:${canonicalize(value[key])}`)
    .join(',');
  return `{${body}}`;
};

/** Read at call time, not module load, so a test can stub it. */
const subtleCrypto = () =>
  (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) || null;

const toHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

/**
 * Both `crypto.subtle` and `TextEncoder` are required, and both are far older
 * than any browser this admin surface supports — a context missing either is a
 * non-secure context missing both. They are checked together so the degraded
 * path is one path, not two.
 */
const canHash = () => Boolean(subtleCrypto()) && typeof TextEncoder === 'function';

/**
 * SHA-256 of the canonicalized document, lowercase hex.
 *
 * Returns `null` when `crypto.subtle` is unavailable. That happens in a
 * non-secure context (plain http on a host that is not localhost), and it must
 * degrade rather than fail: losing duplicate detection is a small thing,
 * refusing the whole import over it is not.
 *
 * @param {Object} document
 * @returns {Promise<string|null>}
 */
export const contentHash = async (document) => {
  if (!canHash()) return null;

  const bytes = new TextEncoder().encode(canonicalize(document));
  const digest = await subtleCrypto().digest('SHA-256', bytes);
  return toHex(digest);
};
